import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Alert, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { DASHBOARD_ERRORS } from "../../../common/constants/dashboardModule/dashboardConstants.js";
import { OPTIN_TABS, OPTIN_STRINGS, formatDateTime } from "../../../common/constants/dashboardModule/optinConstants.js";
import {
    fetchOptinData,
    fetchOptoutConversations,
} from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import OptinDailyAreaChart from "./optinchart.jsx";

// ── Constants ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";
const PINK  = "#D4537E";
const AMBER = "#BA7517";

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
    if (!val) return null;
    const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

const formatDate = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return val || "—";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

// ── Component ──────────────────────────────────────────────────────
const Optin = () => {
    const today          = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate, setStartDate] = useState(firstDayOfMonth);
    const [endDate,   setEndDate]   = useState(today);
    const [optinType, setOptinType] = useState(OPTIN_TABS.TEXTS);
    const [optinData, setOptinData] = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState("");
    const [page,      setPage]      = useState(0);
    const [sortKey,   setSortKey]   = useState(null);
    const [sortDir,   setSortDir]   = useState("asc");
    const [drill,     setDrill]     = useState(null);

    const isEmail = optinType === OPTIN_TABS.EMAILS;

    // ── Column config (dynamic on tab) ────────────────────────────
    const COLS = useMemo(() => [
        { key: "CustomerName", label: "Customer Name",                       width: 200, align: "left"  },
        { key: "CustomerID",   label: "Account ID",                          width: 130, align: "left"  },
        { key: isEmail ? "EmailAddress" : "PhoneNumber",
                               label: isEmail ? "Email Address" : "Phone",   width: 220, align: "left"  },
        { key: "Date",         label: "Opt-Out Date",                        width: 130, align: "left"  },
    ], [isEmail]);

    // 40px drill icon column at the start
    const TABLE_COLS  = useMemo(() => [{ key: "_drill", label: "", width: 40, align: "left" }, ...COLS], [COLS]);
    const GRID_COLS   = TABLE_COLS.map(c => `${c.width}px`).join(" ");
    const TABLE_WIDTH = TABLE_COLS.reduce((s, c) => s + c.width, 0) + 64;
    const ROWS_PER_PAGE = 25;

    useEffect(() => {
        load(firstDayOfMonth, today, OPTIN_TABS.TEXTS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (start, end, type) => {
        try {
            setLoading(true); setError(""); setPage(0);
            setOptinData((await fetchOptinData({
                startDate: formatDateTime(start),
                endDate:   formatDateTime(end),
                optinType: type,
            })) || []);
        } catch (e) {
            console.error(DASHBOARD_ERRORS.FETCH_OPTIN_ERROR, e);
            setError("Failed to load opt-in data.");
        } finally {
            setLoading(false);
        }
    };

    const handleSortCol = (key) => {
        if (key === "_drill") return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortKey) return optinData;
        return [...optinData].sort((a, b) => {
            if (sortKey === "Date") {
                const da = parseLocalDate(a.Date);
                const db = parseLocalDate(b.Date);
                const cmp = (da?.getTime() || 0) - (db?.getTime() || 0);
                return sortDir === "asc" ? cmp : -cmp;
            }
            const av = a[sortKey], bv = b[sortKey];
            const cmp = String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [optinData, sortKey, sortDir]);

    const pagedData  = useMemo(() =>
        sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sortedData, page]);

    const totalPages = Math.ceil(optinData.length / ROWS_PER_PAGE);

    const kpis = useMemo(() => {
        const total = optinData.length;
        const days  = Math.max(1, Math.round(
            (endDate - startDate) / (1000 * 60 * 60 * 24)
        ));

        let recent = null;
        let recentRow = null;
        optinData.forEach(r => {
            const d = parseLocalDate(r.Date);
            if (d && (!recent || d > recent)) { recent = d; recentRow = r; }
        });

        return {
            total,
            avgDay: total > 0 ? (total / days).toFixed(1) : "0",
            recent: recent
                ? `${String(recent.getMonth() + 1).padStart(2, "0")}/${String(recent.getDate()).padStart(2, "0")}/${recent.getFullYear()}`
                : "—",
            recentRow,
        };
    }, [optinData, startDate, endDate]);

    // ── Drill-down ────────────────────────────────────────────────
    // customerId provided → that customer's opt-out conversation
    // customerId null      → all opt-outs in the date range
    const openDrill = useCallback(({ customerId = null, customerName = null, title } = {}) => {
        const resolvedTitle = title
            ?? (customerName
                ? `Opt-out conversation: ${customerName}`
                : "All opt-out conversations");

        setDrill({
            title: resolvedTitle,
            metric: customerName
                ? { label: "Customer", value: customerName, color: PINK }
                : { label: "Opt-outs", value: optinData.length.toLocaleString(), color: PINK },
            context: [
                startDate && { label: "From", value: formatDate(startDate) },
                endDate   && { label: "To",   value: formatDate(endDate)   },
            ].filter(Boolean),
            channel: optinType,
            fetchData: () => fetchOptoutConversations({
                customerId,
                startDate: startDate ? startDate.toISOString() : null,
                endDate:   endDate   ? endDate.toISOString()   : null,
                optinType,
            }),
        });
    }, [optinData.length, startDate, endDate, optinType]);

    // ── CSV export ────────────────────────────────────────────────
    const exportCsv = () => {
        if (!sortedData.length) return;

        const headers = COLS.map(c => csvEscape(c.label));
        const rows = sortedData.map(row =>
            COLS.map(col => {
                if (col.key === "Date") return csvEscape(formatDate(row.Date));
                return csvEscape(row[col.key]);
            }).join(",")
        );
        const csv  = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);

        const now = new Date();
        const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
        const channel = optinType.toLowerCase();

        const a = document.createElement("a");
        a.href     = url;
        a.download = `optouts_${channel}_${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    const exportDisabled = loading || sortedData.length === 0;

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Filters ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent label={STRINGS.START_DATE} date={startDate} onDateChange={setStartDate} marginRight={0} />
                <DatePickerComponent label={STRINGS.END_DATE}   date={endDate}
                    onDateChange={d => { if (d >= startDate) setEndDate(d); }} marginRight={0} />
                <Box onClick={() => load(startDate, endDate, optinType)} sx={{
                    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none",
                    background: BLUE, color: "#fff", fontWeight: 500,
                    alignSelf: "flex-end", mb: "1px",
                    "&:hover": { background: "#2a6db5" },
                }}>Search</Box>

                <Box
                    onClick={exportDisabled ? undefined : exportCsv}
                    title={exportDisabled ? "No data to export" : "Export current view to CSV"}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        userSelect: "none",
                        border: "0.5px solid",
                        borderColor: exportDisabled ? "divider" : BLUE,
                        background: "transparent",
                        color: exportDisabled ? "text.disabled" : BLUE,
                        fontWeight: 500,
                        cursor: exportDisabled ? "default" : "pointer",
                        opacity: exportDisabled ? 0.5 : 1,
                        alignSelf: "flex-end", mb: "1px",
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        transition: "all .15s",
                        "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}
                >
                    <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
                    Export CSV
                </Box>

                <Box sx={{
                    display: "flex", ml: "auto",
                    border: "0.5px solid", borderColor: "divider",
                    borderRadius: "20px", overflow: "hidden",
                    alignSelf: "flex-end",
                }}>
                    {[OPTIN_TABS.TEXTS, OPTIN_TABS.EMAILS].map(ch => (
                        <Box key={ch} onClick={() => { setOptinType(ch); load(startDate, endDate, ch); }} sx={{
                            px: "14px", py: "5px", fontSize: 13,
                            fontWeight:  optinType === ch ? 500 : 400,
                            cursor: "pointer", userSelect: "none",
                            background:  optinType === ch ? BLUE : "transparent",
                            color:       optinType === ch ? "#fff" : "text.secondary",
                            transition:  "all .15s",
                        }}>{ch}</Box>
                    ))}
                </Box>
            </Box>

            {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && optinData.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {OPTIN_STRINGS.NO_RESULTS}
                </Typography>
            )}

            {!loading && optinData.length > 0 && (
                <>
                    {/* ── KPIs — clickable where they map to a real conversation set ── */}
                    <SectionTitle>Summary</SectionTitle>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {[
                            {
                                label: "Total opt-outs", value: kpis.total.toLocaleString(), color: PINK,
                                onClick: () => openDrill({}),
                            },
                            {
                                label: "Daily average",  value: kpis.avgDay, color: AMBER,
                                onClick: undefined,
                            },
                            {
                                label: "Most recent opt-out", value: kpis.recent, color: BLUE,
                                onClick: kpis.recentRow
                                    ? () => openDrill({
                                        customerId:   kpis.recentRow.CustomerID,
                                        customerName: kpis.recentRow.CustomerName,
                                      })
                                    : undefined,
                            },
                        ].map(k => (
                            <Box key={k.label} onClick={k.onClick} sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", flex: 1, minWidth: 0,
                                cursor: k.onClick ? "pointer" : "default",
                                transition: "background .15s",
                                "&:hover": k.onClick ? { background: "#efefef" } : {},
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{k.label}</Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                                    {k.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* ── Chart ── */}
                    <OptinDailyAreaChart optinData={optinData} />

                    {/* ── Table ── */}
                    <SectionTitle>Detail</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            alignItems: "center",
                        }}>
                            {TABLE_COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    sx={{
                                        textAlign: col.align,
                                        cursor: col.key === "_drill" ? "default" : "pointer",
                                        userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": col.key === "_drill" ? {} : { color: BLUE },
                                    }}
                                >
                                    {col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Rows */}
                        {pagedData.map((row, i) => (
                            <Box key={row.CustomerID ?? i} sx={{
                                ...gridSx, py: "7px", fontSize: 12,
                                borderBottom: "0.5px solid", borderColor: "divider",
                                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                "&:hover": { background: "rgba(55,138,221,0.06)" },
                                alignItems: "center",
                            }}>
                                {/* drill icon */}
                                <Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => openDrill({
                                            customerId:   row.CustomerID,
                                            customerName: row.CustomerName,
                                        })}
                                        sx={{ width: 24, height: 24 }}
                                        title="View opt-out conversation for this customer"
                                    >
                                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>

                                {COLS.map(col => (
                                    <Box key={col.key} sx={{
                                        display: "flex", alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        ...(col.key === "CustomerID"   && { fontFamily: "monospace", color: "text.secondary", fontSize: 11 }),
                                        ...(col.key === "Date"         && { color: "text.secondary", whiteSpace: "nowrap" }),
                                        ...(col.key === "CustomerName" && { fontWeight: 500 }),
                                    }}>
                                        {col.key === "Date" ? formatDate(row.Date) : (row[col.key] ?? "—")}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, px: 1 }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, optinData.length)} of {optinData.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Drill-down popup */}
            {drill && (
                <ConversationDrillDownDialog
                    open={!!drill}
                    onClose={() => setDrill(null)}
                    {...drill}
                />
            )}
        </Box>
    );
};

export default Optin;